import {
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  Grid,
  Typography,
} from '@mui/material'
import { SearchBoxProvided } from 'react-instantsearch-core'
import {
  Configure,
  connectInfiniteHits,
  connectSearchBox,
  InstantSearch,
} from 'react-instantsearch-dom'
import { Search } from 'utoniq-ui'

import {
  Artist,
  CurrentUserFragment,
  Maybe,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { ScrollFetcher } from '@/components/organisms/functionals'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import * as algolia from '@/lib/algolia'

const client = algolia.client()

export type State = {
  user: Maybe<CurrentUserFragment>
}

export type Action = {}

type ArtistsCellProps = {
  items: Artist[]
  hasNextPage: boolean
  onFetchMore: () => Promise<void>
}

export const ArtistSearchTemplate: Template<State, Action> = ({ state }) => {
  const { routerNames } = useRouter()

  return (
    <BaseLayout
      margin
      navigation={{
        type: 'global',
        props: {
          hasManagementTask: !!state.user?.hasManagementTask,
          unreadNotificationCount: state.user?.unreadNotificationCount,
          hasSettingTask: !!state.user?.isMembershipPersonalInformationRequired,
        },
      }}
    >
      <Head title={routerNames.ArtistSearch} />
      <InstantSearch
        indexName={algolia.indexes.organizationArtists}
        searchClient={client}
      >
        <HeaderCell />
        <Configure filters={algolia.filters.organizationId} />
        <ArtistsInfiniteHits />
      </InstantSearch>
    </BaseLayout>
  )
}

const SearchBox = connectSearchBox<SearchBoxProvided>(
  ({ refine, currentRefinement }) => {
    const { t } = useTranslation('artistSearch')

    return (
      <Search
        value={currentRefinement}
        fullWidth
        placeholder={t('searchPlaceholder')}
        onChange={e => refine(e.currentTarget.value)}
        size="small"
        sx={{
          minHeight: 40,
          bgcolor: ({ palette }) => `${palette.grey[50]}!important`,
          '.MuiInputBase-root, input': {
            bgcolor: 'unset',
          },
        }}
      />
    )
  }
)

export const HeaderCell: Cell = () => {
  const { t } = useTranslation('artistSearch')
  const { routerPages } = useRouter()

  return (
    <CellFrame
      sx={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 3, py: 3 }}
    >
      <SearchBox />
      <Button variant="text" color="primary" href={routerPages.GlobalArtist}>
        {t('cancel')}
      </Button>
    </CellFrame>
  )
}

const ArtistsInfiniteHits = connectInfiniteHits(
  ({ hits, hasMore, refineNext }) => {
    const items = hits.map(({ objectID, ...item }) => ({
      id: objectID,
      ...item,
    }))
    return (
      <ArtistsCell
        items={items}
        hasNextPage={hasMore}
        onFetchMore={async () => refineNext()}
      />
    )
  }
)

const ArtistsCell: Cell<ArtistsCellProps> = ({
  onFetchMore,
  hasNextPage,
  items,
}) => {
  const { routerPages, generatePath } = useRouter()

  return (
    <CellFrame>
      <ScrollFetcher
        loading={false}
        hasNextPage={hasNextPage}
        onFetchMore={onFetchMore}
        component={Grid}
        componentProps={{ spacing: 5, container: true }}
        itemComponent={Grid}
        itemComponentProps={{ item: true, xs: 6, md: 4 }}
        emptyMessage={<Box />}
        nodes={items?.map(artist => (
          <Card
            sx={{
              display: 'grid',
              gap: 3,
              height: '100%',
              boxShadow: 0,
              bgcolor: 'background.default',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            key={artist.id}
          >
            <CardActionArea
              href={generatePath(routerPages.ArtistTop, {
                artistId: artist.id,
              })}
            >
              <Avatar
                sx={{ width: 158, height: 158, mb: 3 }}
                src={artist.avatar?.smallUri ?? artist.avatar?.uri ?? ''}
                alt={artist.displayName ?? ''}
              />
              <Typography variant="body2" color="text.secondary" align="center">
                {artist.displayName}
              </Typography>
            </CardActionArea>
          </Card>
        ))}
      />
    </CellFrame>
  )
}
